// SCHEDULE_MODEL_PHASE10_20260911
// ALLOW_PDF_IMAGES_V1
import { useEffect, useMemo, useState } from 'react';
import { resolveAssignmentInstanceLabel } from '../../../utils/assignmentLabels';
import RichTextEditor from '../../../components/RichTextEditor';
import StudentInstructorFeedback from './StudentInstructorFeedback';
import ForwardedAssignmentFeedback from './ForwardedAssignmentFeedback';
import ReceivedPeerReviews from './ReceivedPeerReviews';
import SharedScheduleChoiceModal from './SharedScheduleChoiceModal';
import AssignmentGroupMembers from './AssignmentGroupMembers';
import AssignmentInstructions from './AssignmentInstructions';
import MultiFileSubmissionControls from './MultiFileSubmissionControls';
import {
  getSubmissionFileAccept,
  getSubmissionFileExtension,
  getSubmissionFileFormatLabel,
  safeSubmissionFilePart,
  validateConfiguredSubmissionFile
} from '../../../utils/assignmentSubmissionFiles';
import {
  fetchAssignmentGroupLabelsForCourse,
  getAssignmentGroupDisplayName
} from '../../../utils/groupLabels';
import {
  isInlineReviewableFile
} from '../../../utils/reviewablePdf';
import {
  getEffectiveToday,
  isOpenEverything
} from '../../../utils/systemTime';
import {
  expandStudentAssignmentScheduleOccurrences,
  getAssignmentScheduleDuplicateLink,
  getChoiceSelectionsForAssignment,
  getChoiceWindowState,
  getScheduleItemChronologyValue,
  getScheduleItemDates,
  getScheduleItemOccupancy,
  getScheduleItemSource,
  isScheduleChoiceEnabledForAssignment,
  loadStudentAssignmentScheduleContext,
  confirmStudentAssignmentScheduleChoices,
  confirmStudentSharedAssignmentScheduleChoices
} from '../../../utils/studentAssignmentSchedule';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;
const API_URL = process.env.REACT_APP_API_URL;

function apiHeaders(extra = {}) {
  return {
    apikey: SUPABASE_KEY,
    Authorization: 'Bearer ' + SUPABASE_KEY,
    ...extra
  };
}

async function readJson(response, label) {
  const text = await response.text();

  if (!response.ok) {
    throw new Error(
      `${label} failed (${response.status}): ${text || response.statusText}`
    );
  }

  return text ? JSON.parse(text) : null;
}

async function fetchAssignmentGroupMemberships(enrolmentId) {
  if (!enrolmentId) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_group_memberships?enrolment_id=eq.${enrolmentId}&select=course_assignment_id,enrolment_id,group_number`,
    { headers: apiHeaders() }
  );

  return readJson(response, 'Loading assignment group memberships');
}

async function fetchAssignmentSubmissions(enrolmentId) {
  if (!enrolmentId) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_submissions?enrolment_id=eq.${enrolmentId}&select=*,assignment_feedback(*)`,
    { headers: apiHeaders() }
  );

  return readJson(response, 'Loading scheduled assignment submissions');
}

async function createOccurrenceSubmission({
  enrolmentId,
  courseAssignmentId,
  scheduleItemId
}) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_submissions`,
    {
      method: 'POST',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        Prefer: 'return=representation'
      }),
      body: JSON.stringify({
        enrolment_id: enrolmentId,
        course_assignment_id: courseAssignmentId,
        course_assignment_schedule_item_id: scheduleItemId,
        submitted: false,
        extension_days: 0
      })
    }
  );

  return readJson(response, 'Creating scheduled assignment submission');
}

async function updateSubmission(id, data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_submissions?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        Prefer: 'return=representation'
      }),
      body: JSON.stringify(data)
    }
  );

  return readJson(response, 'Saving scheduled assignment submission');
}

async function submitFeedbackResponse(submissionId, responseHtml) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/assignment_feedback?assignment_submission_id=eq.${submissionId}&submitted=eq.true`,
    {
      method: 'PATCH',
      headers: apiHeaders({
        'Content-Type': 'application/json',
        Prefer: 'return=representation'
      }),
      body: JSON.stringify({
        student_response: responseHtml
      })
    }
  );

  const rows = await readJson(response, 'Submitting feedback response');

  if (!rows?.[0]) {
    throw new Error('Instructor feedback record was not found.');
  }

  return rows[0];
}

function formatDate(dateStr) {
  if (!dateStr) return 'Not configured';

  return new Date(`${String(dateStr).slice(0, 10)}T12:00:00`)
    .toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
}

function assignmentLabel(
  assignment,
  allAssignments
) {
  const typeName =
    assignment
      ?.assignment_types
      ?.name ||
    'Assignment';

  const sameType =
    (allAssignments || [])
      .filter(
        candidate =>
          String(
            candidate.assignment_type_id
          ) ===
          String(
            assignment
              ?.assignment_type_id
          )
      );

  return resolveAssignmentInstanceLabel(
    assignment,
    {
      baseName: typeName,
      sameTypeCount:
        sameType.length
    }
  );
}

function normalizeFeedback(feedback) {
  const rows = Array.isArray(feedback)
    ? feedback
    : feedback
      ? [feedback]
      : [];

  if (!rows.length) return null;

  return [...rows].sort((a, b) => {
    const aTime = new Date(
      a.submitted_at || a.updated_at || a.created_at || 0
    ).getTime();
    const bTime = new Date(
      b.submitted_at || b.updated_at || b.created_at || 0
    ).getTime();
    return bTime - aTime;
  })[0] || null;
}

function hasMeaningfulRichText(html) {
  return String(html || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim().length > 0;
}

function isValidHttpsUrl(value) {
  try {
    const parsed = new URL(String(value || '').trim());
    return parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function occurrenceStatus(occurrence, today, bypass) {
  if (bypass) return 'open';

  const openDate =
    occurrence?.effective_open_date ||
    null;

  const dueDate =
    occurrence?.effective_due_date ||
    null;

  /*
   * As with ordinary assignments, an open date
   * is optional. A due date is what makes the
   * submission window configured.
   */
  if (!dueDate) {
    return 'not_configured';
  }

  if (
    openDate &&
    today < openDate
  ) {
    return 'not_open';
  }

  if (today <= dueDate) {
    return 'open';
  }

  return 'closed';
}

function occurrenceStatusLabel(occurrence, today, bypass) {
  const status = occurrenceStatus(occurrence, today, bypass);

  if (status === 'open') return 'Open';
  if (status === 'not_open') return 'Not open';
  if (status === 'closed') return 'Closed';
  return 'Dates not configured';
}

function safeFilePart(value) {
  return String(value || '')
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);
}

function AiPolicyInfo({ permittedAiUses }) {
  return (
    <div style={styles.infoBox}>
      <div style={styles.infoBoxTitle}>
        In this course, AI can be used in only <u>the following ways</u>:
      </div>

      {permittedAiUses.length === 0 ? (
        <div style={styles.noAiAllowed}>
          This course does not currently permit any uses of AI.
        </div>
      ) : (
        <div style={styles.permissionList}>
          {permittedAiUses.map(permission => (
            <div key={permission.id} style={styles.permissionItem}>
              <div style={styles.permissionTitle}>
                {permission.ai_use_types?.label || 'Permitted AI use'}
              </div>
              {permission.ai_use_types?.description && (
                <div style={styles.permissionDescription}>
                  {permission.ai_use_types.description}
                </div>
              )}
              {permission.note && (
                <div style={styles.permissionNote}>
                  Course note: {permission.note}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SharingUrlInfo() {
  return (
    <div style={styles.infoBox}>
      <div style={styles.infoBoxTitle}>
        The Sharing URL is not the same as the URL of your conversation.
      </div>
      <div style={styles.infoText}>
        Use the conversation's Share control, create a sharing link, then paste
        that sharing URL here.
      </div>
    </div>
  );
}

function AiDisclosurePanel({
  aiUsed,
  setAiUsed,
  policyConfirmed,
  setPolicyConfirmed,
  conversationUrl,
  setConversationUrl,
  permittedAiUses,
  radioName = 'scheduled-assignment-ai-used'
}) {
  const [showPolicyInfo, setShowPolicyInfo] = useState(false);
  const [showSharingInfo, setShowSharingInfo] = useState(false);

  function chooseAiUsed(nextValue) {
    setAiUsed(nextValue);

    if (!nextValue) {
      setPolicyConfirmed(false);
      setConversationUrl('');
      setShowPolicyInfo(false);
      setShowSharingInfo(false);
    }
  }

  return (
    <div style={styles.aiDisclosurePanel}>
      <div style={styles.aiDisclosureHeading}>AI Disclosure</div>

      <div style={styles.aiQuestion}>
        Did you use AI in creating this submission?
      </div>

      <div style={styles.radioRow}>
        <label style={styles.radioLabel}>
          <input
            type="radio"
            name={radioName}
            checked={aiUsed === false}
            onChange={() => chooseAiUsed(false)}
          />
          No
        </label>
        <label style={styles.radioLabel}>
          <input
            type="radio"
            name={radioName}
            checked={aiUsed === true}
            onChange={() => chooseAiUsed(true)}
          />
          Yes
        </label>
      </div>

      {aiUsed && (
        <div style={styles.aiYesArea}>
          {permittedAiUses.length === 0 && (
            <div style={styles.noAiAllowed}>
              This course does not permit any uses of AI for this assignment.
            </div>
          )}

          <div style={styles.confirmationRow}>
            <span>
              I confirm that I used AI for this assignment in accordance with
              this course's AI Policy.
            </span>
            <button
              type="button"
              style={styles.infoBtn}
              onClick={() => setShowPolicyInfo(prev => !prev)}
              aria-label="Show permitted AI uses"
            >
              ?
            </button>
            <input
              type="checkbox"
              checked={policyConfirmed}
              onChange={event => setPolicyConfirmed(event.target.checked)}
            />
          </div>

          {showPolicyInfo && (
            <AiPolicyInfo permittedAiUses={permittedAiUses} />
          )}

          <div style={styles.urlRow}>
            <input
              type="url"
              style={styles.urlInput}
              value={conversationUrl}
              onChange={event => setConversationUrl(event.target.value)}
              placeholder="Enter AI Conversation Sharing URL here"
            />
            <button
              type="button"
              style={styles.infoBtn}
              onClick={() => setShowSharingInfo(prev => !prev)}
              aria-label="How to find a sharing URL"
            >
              ?
            </button>
          </div>

          {showSharingInfo && <SharingUrlInfo />}
        </div>
      )}
    </div>
  );
}

function AiDisclosureReadOnly({ submission }) {
  if (typeof submission?.ai_used !== 'boolean') return null;

  return (
    <div style={styles.aiDisclosureReadOnly}>
      <div style={styles.feedbackLabel}>AI Disclosure</div>
      <div>
        Used AI: <strong>{submission.ai_used ? 'Yes' : 'No'}</strong>
      </div>
      {submission.ai_used && submission.ai_conversation_url && (
        <div style={styles.aiDisclosureLinkRow}>
          Conversation sharing URL:{' '}
          <a
            href={submission.ai_conversation_url}
            target="_blank"
            rel="noreferrer"
          >
            {submission.ai_conversation_url}
          </a>
        </div>
      )}
    </div>
  );
}


function ScheduledOccurrenceSection({
  occurrence,
  parentAssignment,
  allAssignments,
  submission,
  source,
  session,
  course,
  enrolment,
  aiDisclosureEnabled,
  permittedAiUses,
  assignmentTypeSetting,
  submissionFileSlots,
  today,
  bypass,
  reloadSubmissions,
  onOpenReading
}) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [aiUsed, setAiUsed] = useState(false);
  const [aiPolicyConfirmed, setAiPolicyConfirmed] = useState(false);
  const [aiConversationUrl, setAiConversationUrl] = useState('');
  const [feedbackResponse, setFeedbackResponse] = useState('');
  const [uploading, setUploading] = useState(false);
  const [savingResponse, setSavingResponse] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const feedback = normalizeFeedback(submission?.assignment_feedback);
  const hasFeedback =
    submission?.submitted && feedback?.submitted === true;
  const status = occurrenceStatus(occurrence, today, bypass);

  const multiFileEnabled =
    assignmentTypeSetting
      ?.multi_file_submission ===
    true;

  useEffect(() => {
    setFeedbackResponse(feedback?.student_response || '');

    if (typeof submission?.ai_used === 'boolean') {
      setAiUsed(submission.ai_used);
      setAiPolicyConfirmed(!!submission.ai_policy_confirmed);
      setAiConversationUrl(submission.ai_conversation_url || '');
    } else {
      setAiUsed(false);
      setAiPolicyConfirmed(false);
      setAiConversationUrl('');
    }

    setSelectedFile(null);
  }, [submission?.id, submission?.ai_used, submission?.ai_policy_confirmed,
      submission?.ai_conversation_url, feedback?.student_response]);

  async function handleFeedbackResponse(event) {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!submission?.submitted || feedback?.submitted !== true) {
      setError('Instructor feedback is no longer available.');
      return;
    }

    if (!hasMeaningfulRichText(feedbackResponse)) {
      setError('Please write a response.');
      return;
    }

    setSavingResponse(true);

    try {
      const updatedFeedback = await submitFeedbackResponse(
        submission.id,
        feedbackResponse.trim()
      );
      setFeedbackResponse(
        updatedFeedback.student_response || feedbackResponse.trim()
      );
      await reloadSubmissions();
      setSuccess('Response submitted.');
    } catch (err) {
      console.error('Feedback response failed:', err);
      setError(err?.message || 'Failed to submit response.');
    } finally {
      setSavingResponse(false);
    }
  }

  async function ensureOccurrenceSubmission() {
    if (submission?.id) {
      return submission;
    }

    const result =
      await createOccurrenceSubmission({
        enrolmentId:
          enrolment.id,

        courseAssignmentId:
          occurrence.id,

        scheduleItemId:
          occurrence
            .course_assignment_schedule_item_id
      });

    if (
      !Array.isArray(result) ||
      !result[0]
    ) {
      throw new Error(
        'Failed to create submission.'
      );
    }

    return result[0];
  }


  function getMultiFileDisclosureData() {
    if (
      aiDisclosureEnabled &&
      aiUsed
    ) {
      if (
        permittedAiUses.length ===
        0
      ) {
        throw new Error(
          'This course does not permit any uses of AI for this assignment.'
        );
      }

      if (!aiPolicyConfirmed) {
        throw new Error(
          'You must confirm that your AI use complied with the course AI Policy.'
        );
      }

      if (
        !isValidHttpsUrl(
          aiConversationUrl
        )
      ) {
        throw new Error(
          'Enter a valid HTTPS AI conversation sharing URL.'
        );
      }
    }

    return aiDisclosureEnabled
      ? {
          ai_used:
            !!aiUsed,

          ai_policy_confirmed:
            aiUsed
              ? !!aiPolicyConfirmed
              : false,

          ai_conversation_url:
            aiUsed
              ? aiConversationUrl.trim()
              : null
        }
      : {};
  }


  async function handleFileSelect(file) {
    if (!file) return;

    try {
      await validateConfiguredSubmissionFile(
        file,
        assignmentTypeSetting
      );

      setSelectedFile(
        file
      );

      setError('');

    } catch (err) {
      setSelectedFile(
        null
      );

      setError(
        err?.message ||
        'That file cannot be submitted.'
      );
    }
  }

  async function handleSubmitSelectedFile() {
    if (!selectedFile) {
      setError('Choose a file to submit.');
      return;
    }

    await handleFileUpload(selectedFile);
  }

  async function handleFileUpload(file) {
    if (!file) return;

    if (!isInlineReviewableFile(file)) {
      setSelectedFile(null);

      setError(
        'Only PDF or Word (.docx) files can be submitted.'
      );

      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setError('File too large. Maximum size is 50MB.');
      return;
    }

    if (status !== 'open') {
      setError('The submission window is not currently open.');
      return;
    }

    if (aiDisclosureEnabled && aiUsed) {
      if (permittedAiUses.length === 0) {
        setError(
          'This course does not permit any uses of AI for this assignment.'
        );
        return;
      }

      if (!aiPolicyConfirmed) {
        setError(
          'You must confirm that your AI use complied with the course AI Policy.'
        );
        return;
      }

      if (!isValidHttpsUrl(aiConversationUrl)) {
        setError('Enter a valid HTTPS AI conversation sharing URL.');
        return;
      }
    }

    const disclosureData = aiDisclosureEnabled
      ? {
          ai_used: !!aiUsed,
          ai_policy_confirmed: aiUsed ? !!aiPolicyConfirmed : false,
          ai_conversation_url: aiUsed ? aiConversationUrl.trim() : null
        }
      : {};

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      let currentSubmission = submission;

      if (!currentSubmission) {
        const result = await createOccurrenceSubmission({
          enrolmentId: enrolment.id,
          courseAssignmentId: occurrence.id,
          scheduleItemId: occurrence.course_assignment_schedule_item_id
        });

        if (!Array.isArray(result) || !result[0]) {
          throw new Error('Failed to create submission.');
        }

        currentSubmission = result[0];
      }

      const ext = file.name.includes('.')
        ? file.name.split('.').pop()
        : 'file';

      const fileName = `${safeFilePart(
        assignmentLabel(parentAssignment || occurrence, allAssignments)
      )} - ${safeFilePart(occurrence.schedule_source_title)} - ${safeFilePart(
        session.studentCode || session.studentId
      )}.${safeFilePart(ext)}`;

      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = event => {
          const data = String(event.target?.result || '');
          resolve(data.includes(',') ? data.split(',')[1] : data);
        };
        reader.onerror = () =>
          reject(new Error('Could not read the selected file.'));
        reader.readAsDataURL(file);
      });

      const previousDriveFileId =
        currentSubmission
          .drive_file_id || null;

      const previousFileName =
        currentSubmission
          .file_name || null;

      await updateSubmission(
        currentSubmission.id,
        {
          submitted: false,
          submitted_at: null,
          drive_file_id: null,
          file_name: null
        }
      );

      try {
        await fetch(API_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type':
              'application/json'
          },
          body: JSON.stringify({
            action:
              'uploadAssignmentSubmission',

            submissionId:
              currentSubmission.id,

            courseId:
              course.id,

            fileName,
            base64Data,

            mimeType:
              file.type
          })
        });

        let driveFileId = null;

        for (
          let i = 0;
          i < 30;
          i += 1
        ) {
          await new Promise(resolve =>
            setTimeout(resolve, 2000)
          );

          const checkResponse =
            await fetch(
              SUPABASE_URL +
                '/rest/v1/assignment_submissions?id=eq.' +
                currentSubmission.id +
                '&select=drive_file_id',
              {
                headers:
                  apiHeaders()
              }
            );

          const checkData =
            await readJson(
              checkResponse,
              'Checking uploaded assignment file'
            );

          if (
            checkData?.[0]
              ?.drive_file_id
          ) {
            driveFileId =
              checkData[0]
                .drive_file_id;

            break;
          }
        }

        if (!driveFileId) {
          throw new Error(
            'The file upload did not complete. Your assignment has not been submitted. Please try again.'
          );
        }

        await updateSubmission(
          currentSubmission.id,
          {
            submitted: true,

            submitted_at:
              new Date()
                .toISOString(),

            file_name:
              fileName,

            drive_file_id:
              driveFileId,

            ...disclosureData
          }
        );

        await reloadSubmissions();

      } catch (uploadError) {
        try {
          await updateSubmission(
            currentSubmission.id,
            {
              submitted: false,
              submitted_at: null,

              drive_file_id:
                previousDriveFileId,

              file_name:
                previousFileName
            }
          );

        } catch (restoreError) {
          console.error(
            'Could not restore previous scheduled submission metadata:',
            restoreError
          );
        }

        throw uploadError;
      }
      setSelectedFile(null);
      setSuccess('Submitted successfully.');
    } catch (err) {
      console.error('Scheduled assignment submission failed:', err);
      setError(err?.message || 'Failed to submit.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <section style={styles.submissionSection}>
      <div style={styles.submissionSectionHeader}>
        <div>
          {source?.type === 'reading' ? (
            <button
              type="button"
              style={styles.readingTitle}
              onClick={() => onOpenReading(source.reading)}
            >
              {source.title}
            </button>
          ) : (
            <div style={styles.sourceHeading}>{source?.title}</div>
          )}

          {source?.meta && (
            <div style={styles.sourceMeta}>{source.meta}</div>
          )}
        </div>

        <span style={styles.statusPill}>
          {hasFeedback
            ? 'Feedback received'
            : submission?.submitted
              ? 'Submitted'
              : occurrenceStatusLabel(occurrence, today, bypass)}
        </span>
      </div>

      <div style={styles.dueBanner}>
        Opens {formatDate(occurrence.effective_open_date)}
        {' · '}Due {formatDate(occurrence.effective_due_date)}
        {occurrence.effective_extension_days > 0 && (
          <div style={styles.extensionNotice}>
            <strong>{occurrence.effective_extension_days}-day extension</strong>
            {' · '}Original deadline{' '}
            {formatDate(occurrence.effective_base_due_date)}
          </div>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {hasFeedback && (
        <div style={styles.feedbackBox}>
          <StudentInstructorFeedback
            submissionType="assignment"
            submissionId={
              submission?.id
            }
            enrolmentId={
              enrolment.id
            }
            effectiveFeedback={
              feedback
            }
            gradingMode={
              course?.grading_mode
            }
          />
        </div>
      )}

     <ReceivedPeerReviews
        targetSubmissionId={submission?.id}
      />

      {hasFeedback && !feedback.student_response && (
        <form onSubmit={handleFeedbackResponse} style={styles.responseForm}>
          <label style={styles.responseLabel}>
            Your response to instructor feedback
          </label>
          <div style={styles.responseHint}>Optional</div>
          <RichTextEditor
            value={feedbackResponse}
            onChange={setFeedbackResponse}
          />
          <button
            type="submit"
            style={styles.responseBtn}
            disabled={savingResponse}
          >
            {savingResponse ? 'Submitting...' : 'Submit Response'}
          </button>
        </form>
      )}

      {hasFeedback && feedback.student_response && (
        <div style={styles.responseBox}>
          <div style={styles.feedbackLabel}>Your response</div>
          <div
            dangerouslySetInnerHTML={{ __html: feedback.student_response }}
            style={styles.feedbackText}
          />
        </div>
      )}

      <div style={styles.uploadBox}>
        {multiFileEnabled ? (
          <>
            {submission?.submitted && (
              <AiDisclosureReadOnly
                submission={
                  submission
                }
              />
            )}

            <MultiFileSubmissionControls
              submission={
                submission
              }
              slots={
                submissionFileSlots
              }
              courseId={
                course.id
              }
              allowPdfImages={
                assignmentTypeSetting
                  ?.allow_pdf_images ===
                true
              }
              status={
                status
              }
              ensureSubmission={
                ensureOccurrenceSubmission
              }
              buildStoredFileName={
                (
                  slot,
                  file
                ) => {
                  const ext =
                    getSubmissionFileExtension(
                      file
                    ) ||
                    'file';

                  return (
                    safeSubmissionFilePart(
                      assignmentLabel(
                        parentAssignment ||
                        occurrence,
                        allAssignments
                      )
                    ) +
                    ' - ' +
                    safeSubmissionFilePart(
                      occurrence
                        .schedule_source_title
                    ) +
                    ' - ' +
                    safeSubmissionFilePart(
                      slot.label
                    ) +
                    ' - ' +
                    safeSubmissionFilePart(
                      session.studentCode ||
                      session.studentId
                    ) +
                    '.' +
                    ext
                  );
                }
              }
              getFinalizeData={
                getMultiFileDisclosureData
              }
              onSubmitted={
                async () => {
                  await reloadSubmissions();
                }
              }
            >
              {aiDisclosureEnabled && (
                <AiDisclosurePanel
                  aiUsed={
                    aiUsed
                  }
                  setAiUsed={
                    setAiUsed
                  }
                  policyConfirmed={
                    aiPolicyConfirmed
                  }
                  setPolicyConfirmed={
                    setAiPolicyConfirmed
                  }
                  conversationUrl={
                    aiConversationUrl
                  }
                  setConversationUrl={
                    setAiConversationUrl
                  }
                  permittedAiUses={
                    permittedAiUses
                  }
                  radioName={
                    'scheduled-assignment-ai-used-' +
                    occurrence
                      .course_assignment_schedule_item_id
                  }
                />
              )}
            </MultiFileSubmissionControls>

            {submission?.submitted && (
              <div
                style={
                  styles.lockedNote
                }
              >
                Your submission is locked.
                Contact your instructor to resubmit.
              </div>
            )}
          </>

        ) : (
          <>
            {submission?.submitted ? (
              <>
                <div
                  style={
                    styles.submittedBanner
                  }
                >
                  ✓ Submitted
                </div>

                <AiDisclosureReadOnly
                  submission={
                    submission
                  }
                />

                <div
                  style={
                    styles.lockedNote
                  }
                >
                  Your submission is locked.
                  Contact your instructor to resubmit.
                </div>
              </>

            ) : (
              <>
                <div
                  style={
                    styles.notSubmitted
                  }
                >
                  No file submitted yet.
                </div>

                {status ===
                  'open' ? (
                  <>
                    <label
                      style={
                        styles.uploadLabel
                      }
                    >
                      <input
                        type="file"
                        accept={
                          getSubmissionFileAccept(
                            assignmentTypeSetting
                          )
                        }
                        style={{
                          display:
                            'none'
                        }}
                        onChange={
                          event =>
                            handleFileSelect(
                              event
                                .target
                                .files
                                ?.[0]
                            )
                        }
                        disabled={
                          uploading
                        }
                      />

                      <span
                        style={
                          styles.uploadBtn
                        }
                      >
                        {uploading
                          ? 'Uploading...'
                          : 'Choose submission file'}
                      </span>
                    </label>

                    <div
                      style={{
                        marginTop:
                          '6px',
                        color:
                          '#829ab1',
                        fontSize:
                          '11px'
                      }}
                    >
                      Accepted:{' '}
                      {
                        getSubmissionFileFormatLabel(
                          assignmentTypeSetting
                        )
                      }
                    </div>

                    {selectedFile && (
                      <div
                        style={
                          styles.selectedFile
                        }
                      >
                        Selected file:{' '}
                        <strong>
                          {
                            selectedFile.name
                          }
                        </strong>
                      </div>
                    )}

                    {aiDisclosureEnabled && (
                      <AiDisclosurePanel
                        aiUsed={
                          aiUsed
                        }
                        setAiUsed={
                          setAiUsed
                        }
                        policyConfirmed={
                          aiPolicyConfirmed
                        }
                        setPolicyConfirmed={
                          setAiPolicyConfirmed
                        }
                        conversationUrl={
                          aiConversationUrl
                        }
                        setConversationUrl={
                          setAiConversationUrl
                        }
                        permittedAiUses={
                          permittedAiUses
                        }
                        radioName={
                          'scheduled-assignment-ai-used-' +
                          occurrence
                            .course_assignment_schedule_item_id
                        }
                      />
                    )}

                    <button
                      type="button"
                      style={
                        styles.submitFileBtn
                      }
                      disabled={
                        uploading ||
                        !selectedFile ||
                        (
                          aiDisclosureEnabled &&
                          aiUsed &&
                          permittedAiUses.length ===
                            0
                        )
                      }
                      onClick={
                        handleSubmitSelectedFile
                      }
                    >
                      {uploading
                        ? 'Submitting...'
                        : 'Submit'}
                    </button>
                  </>

                ) : (
                  <div
                    style={
                      styles.closedBanner
                    }
                  >
                    {status ===
                      'not_open'
                      ? 'Submissions not yet open.'
                      : status ===
                          'closed'
                        ? 'Submission window has closed.'
                        : 'No submission dates configured.'}
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}

export default function StudentScheduledAssignments({
  session,
  course,
  enrolment,
  assignments,
  assignmentTypeSetting,
  description,
  aiDisclosureEnabled = false,
  permittedAiUses = [],
  allCourseAssignments = [],
  groupRostersByAssignment = {},
  submissionFileSlots = []
}) {
  const [scheduleContext, setScheduleContext] = useState({
    scheduleItems: [],
    scheduleReadings: [],
    scheduleEvents: [],
    duplicateLinks: [],
    choiceSelections: [],
    occupancy: []
  });
  const [groupMemberships, setGroupMemberships] = useState([]);
  const [assignmentGroupLabels, setAssignmentGroupLabels] =
    useState({});
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
  const [choiceAssignmentId, setChoiceAssignmentId] = useState(null);
  const [pendingChoiceIds, setPendingChoiceIds] = useState([]);
  const [choiceConfirming, setChoiceConfirming] = useState(false);
  const [sharedChoiceOpen, setSharedChoiceOpen] = useState(false);
  const [pendingSharedChoiceIds, setPendingSharedChoiceIds] = useState({});
  const [sharedChoiceConfirming, setSharedChoiceConfirming] = useState(false);
  const [savingChoice, setSavingChoice] = useState(false);
  const [choiceError, setChoiceError] = useState('');
  const [previewReading, setPreviewReading] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const today = getEffectiveToday();
  const bypass = isOpenEverything(session);

  useEffect(() => {
    let cancelled = false;

    fetchAssignmentGroupLabelsForCourse(
      course.id
    )
      .then(labels => {
        if (!cancelled) {
          setAssignmentGroupLabels(
            labels || {}
          );
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.error(
            'Loading Assignment Group names failed:',
            err
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [course.id]);

  function assignmentGroupName(
    assignmentId,
    groupNumber
  ) {
    return getAssignmentGroupDisplayName(
      assignmentGroupLabels,
      assignmentId,
      groupNumber
    );
  }

  async function reloadScheduleContext() {
    const context = await loadStudentAssignmentScheduleContext({
      courseIds: [course.id],
      enrolments: [{ ...enrolment, course_id: course.id }],
      includeChoiceState: !session?.isMaster
    });
    setScheduleContext(context);
    return context;
  }

  async function reloadSubmissions() {
    const rows = await fetchAssignmentSubmissions(enrolment.id);
    setSubmissions(Array.isArray(rows) ? rows : []);
    return rows;
  }

  useEffect(() => {
    let cancelled = false;

    setLoading(true);

    Promise.all([
      loadStudentAssignmentScheduleContext({
        courseIds: [course.id],
        enrolments: [{ ...enrolment, course_id: course.id }],
        includeChoiceState: !session?.isMaster
      }),
      fetchAssignmentGroupMemberships(enrolment.id),
      fetchAssignmentSubmissions(enrolment.id)
    ])
      .then(([context, memberships, submissionRows]) => {
        if (cancelled) return;
        setScheduleContext(context);
        setGroupMemberships(Array.isArray(memberships) ? memberships : []);
        setSubmissions(Array.isArray(submissionRows) ? submissionRows : []);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Scheduled assignment load failed:', err);
        setError(err?.message || 'Could not load this assignment.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
   }, [course.id, enrolment.id, enrolment, session?.isMaster]);

  useEffect(() => {
    async function refreshSubmissionState() {
      try {
        await Promise.all([reloadSubmissions(), reloadScheduleContext()]);
      } catch (err) {
        console.error('Could not refresh scheduled assignments:', err);
      }
    }

    window.addEventListener('focus', refreshSubmissionState);
    return () => window.removeEventListener('focus', refreshSubmissionState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrolment.id, course.id]);

  const occurrences = useMemo(
    () =>
      expandStudentAssignmentScheduleOccurrences({
        assignments,
        assignmentTypeSettings: [assignmentTypeSetting],
        enrolments: [{ ...enrolment, course_id: course.id }],
        memberships: groupMemberships,
        submissions,
        scheduleContext,
        isMaster: !!session?.isMaster
      }),
    [
      assignments,
      assignmentTypeSetting,
      enrolment,
      course.id,
      groupMemberships,
      submissions,
      scheduleContext,
      session?.isMaster
    ]
  );

  function getDuplicateLink(assignmentId) {
    return getAssignmentScheduleDuplicateLink(
      scheduleContext,
      assignmentId
    );
  }

  function getEffectiveChoiceEnabled(assignment) {
    return isScheduleChoiceEnabledForAssignment({
      assignmentId: assignment?.id,
      setting: assignmentTypeSetting,
      context: scheduleContext,
      isMaster: !!session?.isMaster
    });
  }

  function studentCanChoose(assignment) {
    if (
      !getEffectiveChoiceEnabled(assignment) ||
      session?.isMaster
    ) {
      return false;
    }

    let link = getDuplicateLink(assignment?.id);
    if (!link) return true;

    const seen = new Set();

    while (link?.sync_kind === 'shared_instance') {
      const sourceId = String(link.source_course_assignment_id || '');
      if (!sourceId || seen.has(sourceId)) return false;
      seen.add(sourceId);
      link = getDuplicateLink(sourceId);
    }

    // Same-type shared followers make their own per-instance choice from the
    // shared pool. An ordinary Duplicate source still owns its choice.
    return !link;
  }

  function duplicateSourceLabel(link) {
    if (!link) return 'the related assignment';

    const base = link.source_assignment_type_name || 'Assignment';
    const instance = Number(link.source_instance_number) || 0;
    return instance > 0 ? `${base} ${instance}` : base;
  }

  const choiceWindow = getChoiceWindowState(
    assignmentTypeSetting,
    today,
    bypass
  );

  const requiredCount = 1;

  const sharedChoiceMode =
    assignmentTypeSetting?.schedule_choice_enabled === true &&
    assignmentTypeSetting?.schedule_shared_across_instances !== false &&
    assignments.length > 1;

  function getLinkedItems(assignmentId) {
    const assignment = assignments.find(
      row => String(row.id) === String(assignmentId)
    );
    const mode =
      getEffectiveChoiceEnabled(assignment)
        ? 'choice'
        : 'single';

    return (scheduleContext.scheduleItems || [])
      .filter(
        item =>
          String(item.course_assignment_id) === String(assignmentId) &&
          item.configuration_mode === mode
      )
      .sort(
        (a, b) => {
          const aTime =
            getScheduleItemChronologyValue(
              a,
              scheduleContext
            );

          const bTime =
            getScheduleItemChronologyValue(
              b,
              scheduleContext
            );


          if (
            aTime !== null &&
            bTime !== null &&
            aTime !== bTime
          ) {
            return aTime - bTime;
          }


          if (
            aTime !== null &&
            bTime === null
          ) {
            return -1;
          }


          if (
            aTime === null &&
            bTime !== null
          ) {
            return 1;
          }


          return (
            (Number(a.display_order) || 0) -
            (Number(b.display_order) || 0)
          );
        }
      );
  }

  function getAssignmentOccurrences(assignmentId) {
    return occurrences.filter(
      occurrence => String(occurrence.id) === String(assignmentId)
    );
  }

  function getSelections(assignmentId) {
    return getChoiceSelectionsForAssignment({
      context: scheduleContext,
      enrolmentId: enrolment.id,
      assignmentId
    });
  }

  function getMembership(assignmentId) {
    return groupMemberships.find(
      row => String(row.course_assignment_id) === String(assignmentId)
    ) || null;
  }

  function assignmentUsesGroups(assignment) {
    return (
      assignment?.groups_enabled === true &&
      Number(assignment?.group_count) > 0
    );
  }

  function getOccurrenceSubmission(occurrence) {
    return submissions.find(
      row =>
        String(row.course_assignment_id) === String(occurrence.id) &&
        String(row.course_assignment_schedule_item_id) ===
          String(occurrence.course_assignment_schedule_item_id)
    ) || null;
  }

  function getParentExtensionDays(assignmentId) {
    const parent = submissions.find(
      row =>
        String(row.course_assignment_id) === String(assignmentId) &&
        !row.course_assignment_schedule_item_id &&
        !row.structured_form_instance_id
    );

    return Math.max(0, Number(parent?.extension_days) || 0);
  }

  function getChoiceNoun(linkedItems, plural = false) {
    const types = new Set(linkedItems.map(item => item.item_type));
    const base = types.size === 1
      ? types.has('reading')
        ? 'reading'
        : 'event'
      : 'item';
    return plural ? `${base}s` : base;
  }

  function getSourceForItem(item) {
    return getScheduleItemSource(item, scheduleContext);
  }

  function getSourceForOccurrence(occurrence) {
    const item = (scheduleContext.scheduleItems || []).find(
      row =>
        String(row.id) ===
        String(occurrence.course_assignment_schedule_item_id)
    );
    return getSourceForItem(item);
  }

  function openChoice(assignment) {
    if (!studentCanChoose(assignment)) return;

    if (sharedChoiceMode) {
      const allComplete = assignments.every(
        item => getSelections(item.id).length >= requiredCount
      );

      if (allComplete) return;

      setSharedChoiceOpen(true);
      setPendingSharedChoiceIds({});
      setSharedChoiceConfirming(false);
      setChoiceError('');
      setError('');
      setSuccess('');
      return;
    }

    const existingCount = getSelections(assignment.id).length;
    const remaining = Math.max(0, requiredCount - existingCount);

    if (remaining === 0) return;

    setChoiceAssignmentId(assignment.id);
    setPendingChoiceIds([]);
    setChoiceConfirming(false);
    setChoiceError('');
    setError('');
    setSuccess('');
  }

  function closeChoice() {
    if (savingChoice) return;
    setChoiceAssignmentId(null);
    setPendingChoiceIds([]);
    setChoiceConfirming(false);
    setChoiceError('');
  }

  function togglePendingChoice(itemId, remaining) {
    setPendingChoiceIds(current => {
      const exists = current.some(id => String(id) === String(itemId));

      if (exists) {
        return current.filter(id => String(id) !== String(itemId));
      }

      if (remaining === 1) {
        return [itemId];
      }

      if (current.length >= remaining) {
        return current;
      }

      return [...current, itemId];
    });
  }

  async function confirmChoice(assignment) {
    setSavingChoice(true);
    setChoiceError('');

    try {
      await confirmStudentAssignmentScheduleChoices({
        enrolmentId: enrolment.id,
        assignmentId: assignment.id,
        scheduleItemIds: pendingChoiceIds
      });

      await reloadScheduleContext();
      setChoiceAssignmentId(null);
      setPendingChoiceIds([]);
      setChoiceConfirming(false);
      setSuccess('Choice confirmed. Student choices are final.');
    } catch (err) {
      console.error('Final assignment choice failed:', err);
      setChoiceError(err?.message || 'Could not confirm that choice.');
      await reloadScheduleContext().catch(() => {});
    } finally {
      setSavingChoice(false);
    }
  }

  function closeSharedChoice() {
    if (savingChoice) return;
    setSharedChoiceOpen(false);
    setPendingSharedChoiceIds({});
    setSharedChoiceConfirming(false);
    setChoiceError('');
  }

  async function confirmSharedChoices() {
    setSavingChoice(true);
    setChoiceError('');

    try {
      const choices = assignments
        .slice()
        .sort(
          (a, b) =>
            (Number(a.instance_number) || 0) -
              (Number(b.instance_number) || 0) ||
            String(a.id).localeCompare(String(b.id))
        )
        .map(assignment => ({
          course_assignment_id: assignment.id,
          schedule_item_ids:
            pendingSharedChoiceIds[assignment.id] || []
        }));

      await confirmStudentSharedAssignmentScheduleChoices({
        enrolmentId: enrolment.id,
        assignmentTypeId: assignments[0]?.assignment_type_id,
        choices
      });

      await reloadScheduleContext();
      setSharedChoiceOpen(false);
      setPendingSharedChoiceIds({});
      setSharedChoiceConfirming(false);
      setSuccess('Choices confirmed. Student choices are final.');
    } catch (err) {
      console.error('Shared assignment choices failed:', err);
      setChoiceError(err?.message || 'Could not confirm those choices.');
      await reloadScheduleContext().catch(() => {});
    } finally {
      setSavingChoice(false);
    }
  }

  function getListStatus(assignment) {
    const selections = getSelections(assignment.id);
    const duplicateLink = getDuplicateLink(assignment.id);
    const effectiveChoiceEnabled = getEffectiveChoiceEnabled(assignment);

    if (
      duplicateLink &&
      !studentCanChoose(assignment) &&
      effectiveChoiceEnabled &&
      selections.length === 0
    ) {
      return 'Waiting for linked choice';
    }

    if (studentCanChoose(assignment) && selections.length < requiredCount) {
      if (assignmentUsesGroups(assignment) && !getMembership(assignment.id)) {
        return 'Group not assigned';
      }
      if (choiceWindow.state === 'not_open') return 'Choice not open';
      if (choiceWindow.state === 'closed') return 'Choice closed';
      return 'Choice required';
    }

    const assignmentOccurrences = getAssignmentOccurrences(assignment.id);
    if (!assignmentOccurrences.length) return 'Not available';

    const rows = assignmentOccurrences.map(getOccurrenceSubmission);
    const submittedCount = rows.filter(row => row?.submitted).length;
    const feedbackCount = rows.filter(
      row => row?.submitted && normalizeFeedback(row?.assignment_feedback)?.submitted
    ).length;

    if (feedbackCount === assignmentOccurrences.length) return 'Feedback received';
    if (submittedCount === assignmentOccurrences.length) return 'Submitted';
    if (submittedCount > 0) {
      return `${submittedCount}/${assignmentOccurrences.length} submitted`;
    }

    const statuses = assignmentOccurrences.map(occurrence =>
      occurrenceStatus(occurrence, today, bypass)
    );
    if (statuses.includes('open')) return 'Open';
    if (statuses.every(status => status === 'not_open')) return 'Not yet open';
    if (statuses.every(status => status === 'closed')) return 'Closed';
    return 'Scheduled';
  }

  function renderReadingTitle(source, style = styles.readingTitle) {
    return (
      <button
        type="button"
        style={style}
        onClick={() => setPreviewReading(source?.reading || null)}
      >
        {source?.title || 'Reading'}
      </button>
    );
  }

  if (loading) {
    return <div style={styles.muted}>Loading...</div>;
  }

  const selectedAssignment = assignments.find(
    assignment => String(assignment.id) === String(selectedAssignmentId)
  ) || null;

  const choiceAssignment = assignments.find(
    assignment => String(assignment.id) === String(choiceAssignmentId)
  ) || null;

  const typeName = assignments?.[0]?.assignment_types?.name || 'Assignment';

  if (selectedAssignment) {
    const linkedItems = getLinkedItems(selectedAssignment.id);
    const selections = getSelections(selectedAssignment.id);
    const selectedIds = new Set(
      selections.map(selection =>
        String(selection.course_assignment_schedule_item_id)
      )
    );
    const selectedItems = linkedItems.filter(item => selectedIds.has(String(item.id)));
    const assignmentOccurrences = getAssignmentOccurrences(selectedAssignment.id);
    const membership = getMembership(selectedAssignment.id);
    const usesGroups = assignmentUsesGroups(selectedAssignment);
    const remainingCount = Math.max(0, requiredCount - selections.length);
    const choiceNoun = getChoiceNoun(linkedItems, requiredCount !== 1);
    const extensionDays = getParentExtensionDays(selectedAssignment.id);
    const duplicateLink = getDuplicateLink(selectedAssignment.id);
    const effectiveChoiceEnabled =
      getEffectiveChoiceEnabled(selectedAssignment);
    const canChooseHere = studentCanChoose(selectedAssignment);

    return (
      <div style={styles.wrap}>
        <button
          type="button"
          style={styles.backLink}
          onClick={() => {
            setSelectedAssignmentId(null);
            setError('');
            setSuccess('');
          }}
        >
          ← Back to assignments
        </button>

        <h2 style={styles.heading}>
          {assignmentLabel(selectedAssignment, assignments)}
        </h2>

        <AssignmentInstructions
          html={description}
        />

        <ForwardedAssignmentFeedback
          destinationAssignmentId={selectedAssignment.id}
          allAssignments={allCourseAssignments}
          submissions={submissions}
        />

        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}

        {canChooseHere && (
          <section style={styles.choiceSummaryBox}>
            <div style={styles.choiceSummaryHeader}>
              <div style={styles.choiceSummaryTitle}>
                {selections.length >= requiredCount
                  ? `Selected ${getChoiceNoun(linkedItems, selections.length !== 1)}`
                  : `Choose ${choiceNoun}`}
              </div>
              {selections.length > 0 && (
                <span style={styles.finalPill}>Final</span>
              )}
            </div>

            {selectedItems.length > 0 && (
              <div style={styles.selectedChoiceList}>
                {selectedItems.map(item => {
                  const source = getSourceForItem(item);
                  const dates = getScheduleItemDates(
                    item,
                    assignmentTypeSetting,
                    scheduleContext,
                    extensionDays
                  );

                  return (
                    <div key={item.id} style={styles.selectedChoiceRow}>
                      <span style={styles.typePill}>{source.typeLabel}</span>
                      <div style={styles.selectedChoiceMain}>
                        {source.type === 'reading'
                          ? renderReadingTitle(source)
                          : <div style={styles.optionTitle}>{source.title}</div>}
                        {source.meta && (
                          <div style={styles.optionMeta}>{source.meta}</div>
                        )}
                        <div style={styles.dateMeta}>
                          Opens {formatDate(dates.openDate)}
                          {' · '}Due {formatDate(dates.dueDate)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {selections.length < requiredCount && (
              <>
                {usesGroups && !membership ? (
                  <div style={styles.warning}>
                    You cannot make this choice until you are assigned to an assignment group.
                  </div>
                ) : (
                  <>
                    <div style={styles.choiceWindowText}>
                      {choiceWindow.message}
                    </div>

                    {choiceWindow.editable && !bypass && (
                      <button
                        type="button"
                        style={styles.primaryBtn}
                        onClick={() => openChoice(selectedAssignment)}
                      >
                        {sharedChoiceMode
                          ? `Choose for all ${assignments.length} assignments`
                          : selections.length > 0
                            ? `Complete choice (${remainingCount} remaining)`
                            : `Choose ${choiceNoun}`}
                      </button>
                    )}

                    <div style={styles.finalChoiceNote}>
                      {sharedChoiceMode
                        ? 'All assignment-instance choices are confirmed together. Once confirmed, student choices cannot be changed.'
                        : 'Once confirmed, student choices cannot be changed.'}
                    </div>
                  </>
                )}
              </>
            )}
          </section>
        )}

        {duplicateLink && !canChooseHere && effectiveChoiceEnabled && (
          <section style={styles.choiceSummaryBox}>
            <div style={styles.choiceSummaryHeader}>
              <div style={styles.choiceSummaryTitle}>
                Linked choice
              </div>
            </div>

            {selectedItems.length > 0 ? (
              <div style={styles.selectedChoiceList}>
                {selectedItems.map(item => {
                  const source = getSourceForItem(item);
                  const dates = getScheduleItemDates(
                    item,
                    assignmentTypeSetting,
                    scheduleContext,
                    extensionDays
                  );

                  return (
                    <div key={item.id} style={styles.selectedChoiceRow}>
                      <span style={styles.typePill}>{source.typeLabel}</span>
                      <div style={styles.selectedChoiceMain}>
                        {source.type === 'reading'
                          ? renderReadingTitle(source)
                          : <div style={styles.optionTitle}>{source.title}</div>}
                        {source.meta && (
                          <div style={styles.optionMeta}>{source.meta}</div>
                        )}
                        <div style={styles.dateMeta}>
                          Opens {formatDate(dates.openDate)}
                          {' · '}Due {formatDate(dates.dueDate)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={styles.choiceWindowText}>
                This assignment follows the choice made for{' '}
                <strong>{duplicateSourceLabel(duplicateLink)}</strong>.
                The matching assignment item will appear here once that choice
                is set.
              </div>
            )}
          </section>
        )}

        <div style={styles.inlineSubmissions}>
          {assignmentOccurrences.length === 0 ? (
            <div style={styles.emptyBox}>
              {duplicateLink && !canChooseHere && effectiveChoiceEnabled && selections.length === 0
                ? `This assignment will use the same choice as ${duplicateSourceLabel(duplicateLink)} once that choice is set.`
                : canChooseHere && selections.length < requiredCount
                  ? `Confirm your ${choiceNoun} to see the submission here.`
                  : 'No linked assignment items are currently available.'}
            </div>
          ) : (
            assignmentOccurrences.map(occurrence => {
              const source = getSourceForOccurrence(occurrence);

              return (
                <ScheduledOccurrenceSection
                  key={occurrence.student_assignment_occurrence_key}
                  occurrence={occurrence}
                  parentAssignment={selectedAssignment}
                  allAssignments={assignments}
                  submission={getOccurrenceSubmission(occurrence)}
                  source={source}
                  session={session}
                  course={course}
                  enrolment={enrolment}
                  aiDisclosureEnabled={aiDisclosureEnabled}
                  permittedAiUses={permittedAiUses}
                  assignmentTypeSetting={assignmentTypeSetting}
                  submissionFileSlots={submissionFileSlots}
                  today={today}
                  bypass={bypass}
                  reloadSubmissions={reloadSubmissions}
                  onOpenReading={setPreviewReading}
                />
              );
            })
          )}
        </div>

        <AssignmentGroupMembers
          group={
            groupRostersByAssignment[
              selectedAssignment.id
            ] || null
          }
          assignmentId={selectedAssignment.id}
          courseId={course.id}
          session={session}
        />

        {previewReading && (
          <ReadingPreview
            reading={previewReading}
            onClose={() => setPreviewReading(null)}
          />
        )}

        {choiceAssignment && (
          <ChoiceModal
            assignment={choiceAssignment}
            assignmentTitle={assignmentLabel(choiceAssignment, assignments)}
            linkedItems={getLinkedItems(choiceAssignment.id)}
            existingSelections={getSelections(choiceAssignment.id)}
            membership={getMembership(choiceAssignment.id)}
            usesGroups={assignmentUsesGroups(choiceAssignment)}
            requiredCount={requiredCount}
            pendingChoiceIds={pendingChoiceIds}
            setPendingChoiceIds={setPendingChoiceIds}
            confirming={choiceConfirming}
            setConfirming={setChoiceConfirming}
            saving={savingChoice}
            error={choiceError}
            context={scheduleContext}
            setting={assignmentTypeSetting}
            extensionDays={getParentExtensionDays(choiceAssignment.id)}
            onToggle={togglePendingChoice}
            onConfirm={() => confirmChoice(choiceAssignment)}
            onCancel={closeChoice}
            onOpenReading={setPreviewReading}
            groupName={
              getMembership(choiceAssignment.id)
                ? assignmentGroupName(
                    choiceAssignment.id,
                    getMembership(choiceAssignment.id)
                      .group_number
                  )
                : ''
            }
          />
        )}

        {sharedChoiceOpen && (
          <SharedScheduleChoiceModal
            assignments={assignments}
            context={scheduleContext}
            requiredCount={requiredCount}
            pendingByAssignment={pendingSharedChoiceIds}
            setPendingByAssignment={setPendingSharedChoiceIds}
            getSelections={getSelections}
            getMembership={getMembership}
            assignmentUsesGroups={assignmentUsesGroups}
            assignmentLabel={assignment =>
              assignmentLabel(assignment, assignments)
            }
            saving={savingChoice}
            error={choiceError}
            confirming={sharedChoiceConfirming}
            setConfirming={setSharedChoiceConfirming}
            onConfirm={confirmSharedChoices}
            onCancel={closeSharedChoice}
            onOpenReading={setPreviewReading}
            groupNameForAssignment={
              (assignmentId, groupNumber) =>
                assignmentGroupName(
                  assignmentId,
                  groupNumber
                )
            }
          />
        )}
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <h2 style={styles.heading}>{typeName}</h2>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      <div style={styles.assignmentList}>
        {assignments.map(assignment => {
          const linkedItems = getLinkedItems(assignment.id);
          const assignmentOccurrences = getAssignmentOccurrences(assignment.id);
          const selections = getSelections(assignment.id);
          const selectedIds = new Set(
            selections.map(selection =>
              String(selection.course_assignment_schedule_item_id)
            )
          );
          const selectedItems = linkedItems.filter(item =>
            selectedIds.has(String(item.id))
          );

          const duplicateLink = getDuplicateLink(assignment.id);
          const effectiveChoiceEnabled = getEffectiveChoiceEnabled(assignment);
          const canChooseHere = studentCanChoose(assignment);

          let summary = '';
          if (
            duplicateLink &&
            !canChooseHere &&
            effectiveChoiceEnabled &&
            selections.length === 0
          ) {
            summary = `Waiting for ${duplicateSourceLabel(duplicateLink)} choice`;
          } else if (canChooseHere && selections.length < requiredCount) {
            summary = `${selections.length}/${requiredCount} chosen`;
          } else if (selectedItems.length === 1) {
            summary = getSourceForItem(selectedItems[0]).title;
          } else if (selectedItems.length > 1) {
            summary = `${selectedItems.length} selected items`;
          } else if (assignmentOccurrences.length === 1) {
            summary = getSourceForOccurrence(assignmentOccurrences[0]).title;
          } else if (assignmentOccurrences.length > 1) {
            summary = `${assignmentOccurrences.length} submissions`;
          }

          const dueDates = assignmentOccurrences
            .map(occurrence => occurrence.effective_due_date)
            .filter(Boolean)
            .sort();

          return (
            <button
              key={assignment.id}
              type="button"
              style={styles.assignmentListRow}
              onClick={() => {
                setSelectedAssignmentId(assignment.id);
                setError('');
                setSuccess('');
              }}
            >
              <div style={styles.assignmentListMain}>
                <div style={styles.assignmentListTitle}>
                  {assignmentLabel(assignment, assignments)}
                </div>
                {summary && (
                  <div style={styles.assignmentListMeta}>{summary}</div>
                )}
                {dueDates.length > 0 && (
                  <div style={styles.assignmentListDate}>
                    {dueDates.length === 1
                      ? `Due ${formatDate(dueDates[0])}`
                      : `First due ${formatDate(dueDates[0])}`}
                  </div>
                )}
              </div>

              <div style={styles.assignmentListRight}>
                <span style={styles.listStatus}>{getListStatus(assignment)}</span>
                <span style={styles.arrow}>›</span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ReadingPreview({ reading, onClose }) {
  if (!reading) return null;

  return (
    <div style={styles.previewOverlay} onClick={onClose}>
      <div style={styles.previewPanel} onClick={event => event.stopPropagation()}>
        <div style={styles.previewHeader}>
          <div>
            <div style={styles.previewTitle}>{reading.title}</div>
            {reading.author && (
              <div style={styles.previewAuthor}>by {reading.author}</div>
            )}
          </div>
          <button type="button" style={styles.previewClose} onClick={onClose}>
            ×
          </button>
        </div>

        {reading.drive_file_id ? (
          <iframe
            src={`https://drive.google.com/file/d/${reading.drive_file_id}/preview`}
            style={styles.previewFrame}
            title={reading.title}
          />
        ) : (
          <div style={styles.previewNoFile}>No file available.</div>
        )}

        {reading.drive_file_id && (
          <a
            href={`https://drive.google.com/uc?export=download&id=${reading.drive_file_id}`}
            target="_blank"
            rel="noreferrer"
            style={styles.downloadLink}
          >
            Download
          </a>
        )}
      </div>
    </div>
  );
}

function ChoiceModal({
  assignment,
  assignmentTitle,
  linkedItems,
  existingSelections,
  membership,
  usesGroups,
  requiredCount,
  pendingChoiceIds,
  confirming,
  setConfirming,
  saving,
  error,
  context,
  setting,
  extensionDays,
  onToggle,
  onConfirm,
  onCancel,
  onOpenReading,
  groupName
}) {
  const existingIds = new Set(
    existingSelections.map(selection =>
      String(selection.course_assignment_schedule_item_id)
    )
  );
  const remaining = Math.max(0, requiredCount - existingSelections.length);
  const pendingSet = new Set(pendingChoiceIds.map(String));
  const allFinalIds = new Set([...existingIds, ...pendingSet]);
  const finalItems = linkedItems.filter(item => allFinalIds.has(String(item.id)));
  const types = new Set(linkedItems.map(item => item.item_type));
  const noun = types.size === 1
    ? types.has('reading') ? 'reading' : 'event'
    : 'item';
  const pluralNoun = requiredCount === 1 ? noun : `${noun}s`;

  return (
    <div style={styles.modalOverlay} onClick={onCancel}>
      <div style={styles.modalPanel} onClick={event => event.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div>
            <div style={styles.modalTitle}>
              {confirming ? 'Confirm choice' : `Choose ${pluralNoun}`}
            </div>
            <div style={styles.modalSubtitle}>
              {assignmentTitle}
            </div>
          </div>
          <button type="button" style={styles.previewClose} onClick={onCancel}>
            ×
          </button>
        </div>

        {error && <div style={styles.modalError}>{error}</div>}

        {!confirming ? (
          <>
            <div style={styles.modalIntro}>
              {existingSelections.length > 0
                ? `Choose ${remaining} more ${remaining === 1 ? noun : `${noun}s`}. Existing choices are already final.`
                : `Choose ${remaining} ${remaining === 1 ? noun : `${noun}s`}.`}
            </div>

            <div style={styles.modalOptionList}>
              {linkedItems.map(item => {
                const source = getScheduleItemSource(item, context);
                const dates = getScheduleItemDates(item, setting, context, extensionDays || 0);
                const alreadySelected = existingIds.has(String(item.id));
                const pending = pendingSet.has(String(item.id));
                const occupancy = getScheduleItemOccupancy(context, item.id);
                const capacity =
                  item.capacity === null || item.capacity === undefined
                    ? null
                    : Number(item.capacity);
                const full = capacity !== null && occupancy >= capacity;
                const disabled = alreadySelected || (full && !pending) || saving;

                return (
                  <div
                    key={item.id}
                    style={{
                      ...styles.modalOption,
                      ...((alreadySelected || pending) ? styles.modalOptionSelected : {}),
                      ...(disabled && !alreadySelected ? styles.modalOptionDisabled : {})
                    }}
                  >
                    <input
                      type={requiredCount === 1 ? 'radio' : 'checkbox'}
                      name={`final-choice-${assignment.id}`}
                      checked={alreadySelected || pending}
                      disabled={disabled}
                      onChange={() => onToggle(item.id, remaining)}
                    />

                    <div style={styles.modalOptionMain}>
                      <div style={styles.modalOptionHeading}>
                        <span style={styles.typePill}>{source.typeLabel}</span>
                        {source.type === 'reading' ? (
                          <button
                            type="button"
                            style={styles.readingTitle}
                            onClick={event => {
                              event.stopPropagation();
                              onOpenReading(source.reading);
                            }}
                          >
                            {source.title}
                          </button>
                        ) : (
                          <span style={styles.optionTitle}>{source.title}</span>
                        )}
                      </div>

                      {source.meta && (
                        <div style={styles.optionMeta}>{source.meta}</div>
                      )}
                      <div style={styles.dateMeta}>
                        Opens {formatDate(dates.openDate)}
                        {' · '}Due {formatDate(dates.dueDate)}
                      </div>
                    </div>

                    <div style={styles.modalOptionSide}>
                      {alreadySelected && <span style={styles.finalPill}>Final</span>}
                      {!alreadySelected && capacity !== null && (
                        <span style={styles.capacity}>
                          {occupancy}/{capacity} places
                        </span>
                      )}
                      {!alreadySelected && full && (
                        <span style={styles.fullText}>Full</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={styles.modalActions}>
              <button type="button" style={styles.secondaryBtn} onClick={onCancel}>
                Cancel
              </button>
              <button
                type="button"
                style={styles.primaryBtn}
                disabled={pendingChoiceIds.length !== remaining || saving}
                onClick={() => setConfirming(true)}
              >
                Continue
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={styles.confirmChoiceList}>
              {finalItems.map(item => {
                const source = getScheduleItemSource(item, context);
                return (
                  <div key={item.id} style={styles.confirmChoiceRow}>
                    <span style={styles.typePill}>{source.typeLabel}</span>
                    <strong>{source.title}</strong>
                  </div>
                );
              })}
            </div>

            <div style={styles.finalWarning}>
              {usesGroups ? (
                <>
                  You are making this choice for <strong>{groupName || `Group ${membership?.group_number}`}</strong>.
                  {' '}It will apply to <strong>every member of your group</strong> and cannot be changed by students after confirmation.
                </>
              ) : (
                <>
                  This choice is final and cannot be changed by students after confirmation.
                </>
              )}
            </div>

            <div style={styles.modalActions}>
              <button
                type="button"
                style={styles.secondaryBtn}
                disabled={saving}
                onClick={() => setConfirming(false)}
              >
                Back
              </button>
              <button
                type="button"
                style={styles.primaryBtn}
                disabled={saving}
                onClick={onConfirm}
              >
                {saving ? 'Confirming...' : 'Confirm choice'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


const styles = {
  wrap: { color: '#243b53' },
  heading: { margin: '0 0 18px', fontSize: '26px', color: '#243b53' },
  muted: { color: '#627d98', fontSize: '14px' },
  backLink: {
    border: 'none', background: 'none', padding: 0, marginBottom: '14px',
    color: '#215b7d', cursor: 'pointer', fontWeight: '700'
  },
  descriptionContentDirect: {
    border: '1px solid #d9e2ec', borderRadius: '8px', padding: '12px 14px',
    fontSize: '14px', lineHeight: 1.5, marginBottom: '16px', background: 'white'
  },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '8px',
    padding: '10px 14px', color: '#a12a2a', marginBottom: '12px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '8px',
    padding: '10px 14px', color: '#216e45', marginBottom: '12px'
  },
  warning: {
    background: '#fff7e6', border: '1px solid #f1d39a', borderRadius: '8px',
    padding: '10px 12px', color: '#7a5310', fontSize: '13px'
  },
  assignmentList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  assignmentListRow: {
    width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: '14px', border: '1px solid #d9e2ec', borderRadius: '8px', background: 'white',
    padding: '13px 14px', textAlign: 'left', cursor: 'pointer', color: '#243b53'
  },
  assignmentListMain: { minWidth: 0, flex: 1 },
  assignmentListTitle: { fontWeight: '800', fontSize: '15px' },
  assignmentListMeta: { color: '#486581', fontSize: '12px', marginTop: '3px' },
  assignmentListDate: { color: '#829ab1', fontSize: '11px', marginTop: '3px' },
  assignmentListRight: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
  listStatus: { fontSize: '11px', color: '#627d98', fontWeight: '700' },
  arrow: { fontSize: '24px', color: '#829ab1', lineHeight: 1 },
  choiceSummaryBox: {
    border: '1px solid #c9d9e5', borderRadius: '10px', padding: '14px',
    background: '#f7fafc', marginBottom: '18px'
  },
  choiceSummaryHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    gap: '10px', marginBottom: '9px'
  },
  choiceSummaryTitle: { fontSize: '15px', fontWeight: '800', color: '#334e68' },
  finalPill: {
    borderRadius: '999px', padding: '3px 7px', background: '#e7f5ed',
    color: '#216e45', fontSize: '10px', fontWeight: '800', whiteSpace: 'nowrap'
  },
  selectedChoiceList: { display: 'flex', flexDirection: 'column', gap: '7px', marginBottom: '10px' },
  selectedChoiceRow: {
    display: 'flex', gap: '9px', alignItems: 'flex-start', padding: '9px 10px',
    background: 'white', border: '1px solid #e5eaee', borderRadius: '7px'
  },
  selectedChoiceMain: { minWidth: 0, flex: 1 },
  choiceWindowText: { fontSize: '12px', color: '#627d98', marginBottom: '9px' },
  finalChoiceNote: { fontSize: '11px', color: '#7a5310', marginTop: '8px' },
  primaryBtn: {
    border: 'none', borderRadius: '7px', padding: '8px 13px', background: '#215b7d',
    color: 'white', fontWeight: '800', cursor: 'pointer', fontSize: '13px'
  },
  secondaryBtn: {
    border: '1px solid #b7c7d3', borderRadius: '7px', padding: '8px 13px',
    background: 'white', color: '#334e68', fontWeight: '700', cursor: 'pointer', fontSize: '13px'
  },
  inlineSubmissions: { display: 'flex', flexDirection: 'column', gap: '14px' },
  emptyBox: {
    border: '1px dashed #cbd5df', borderRadius: '8px', padding: '14px',
    color: '#627d98', fontSize: '13px', background: '#fbfcfd'
  },
  submissionSection: {
    border: '1px solid #d9e2ec', borderRadius: '10px', padding: '14px', background: 'white'
  },
  submissionSectionHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '10px'
  },
  sourceHeading: { fontSize: '17px', fontWeight: '800', color: '#334e68' },
  sourceMeta: { fontSize: '12px', color: '#627d98', marginTop: '3px' },
  readingTitle: {
    border: 'none', background: 'none', color: '#2f6f9f', fontFamily: 'inherit',
    fontSize: 'inherit', lineHeight: 'inherit', fontWeight: '700', cursor: 'pointer',
    textDecoration: 'underline', padding: 0, textAlign: 'left'
  },
  statusPill: {
    borderRadius: '999px', padding: '4px 8px', background: '#edf2f7',
    color: '#486581', fontSize: '10px', fontWeight: '800', whiteSpace: 'nowrap'
  },
  typePill: {
    height: 'fit-content', padding: '2px 6px', borderRadius: '999px', background: '#e7f1f7',
    color: '#215b7d', fontSize: '9px', fontWeight: '800', textTransform: 'uppercase',
    letterSpacing: '0.05em', flexShrink: 0
  },
  optionTitle: { fontWeight: '700', fontSize: '14px' },
  optionMeta: { color: '#627d98', fontSize: '12px', marginTop: '2px' },
  dateMeta: { color: '#829ab1', fontSize: '11px', marginTop: '4px' },
  capacity: { fontSize: '11px', color: '#627d98' },
  fullText: { fontSize: '11px', color: '#a12a2a', fontWeight: '700' },
  dueBanner: {
    border: '1px solid #d9e2ec', background: '#f7f9fb', borderRadius: '8px',
    padding: '10px 12px', marginBottom: '14px', color: '#334e68', fontSize: '13px'
  },
  extensionNotice: { marginTop: '4px', color: '#7a5310', fontSize: '12px' },
  uploadBox: {
    marginTop: '16px', border: '1px solid #d9e2ec', borderRadius: '10px',
    padding: '14px', background: '#fbfcfd'
  },
  notSubmitted: { color: '#627d98', fontSize: '13px', marginBottom: '10px' },
  submittedBanner: { color: '#216e45', fontWeight: '800', marginBottom: '10px' },
  uploadLabel: { display: 'inline-block', cursor: 'pointer' },
  uploadBtn: {
    display: 'inline-block', borderRadius: '7px', padding: '8px 12px',
    background: '#215b7d', color: 'white', fontWeight: '700', fontSize: '13px'
  },
  selectedFile: { marginTop: '10px', fontSize: '12px', color: '#486581' },
  submitFileBtn: {
    marginTop: '12px', border: 'none', borderRadius: '7px', padding: '8px 14px',
    background: '#215b7d', color: 'white', fontWeight: '800', cursor: 'pointer'
  },
  lockedNote: { marginTop: '9px', color: '#627d98', fontSize: '12px' },
  closedBanner: { marginTop: '10px', color: '#7a5310', fontSize: '12px' },
  feedbackBox: {
    marginBottom: '14px', border: '1px solid #b9dfc9', background: '#f3faf6',
    borderRadius: '9px', padding: '12px'
  },
  feedbackLabel: {
    fontWeight: '800', fontSize: '12px', color: '#486581', marginBottom: '6px',
    textTransform: 'uppercase', letterSpacing: '0.04em'
  },
  feedbackText: { fontSize: '14px', lineHeight: 1.5 },
  completionStatus: { marginTop: '8px', color: '#334e68', fontSize: '13px' },
  responseForm: { marginBottom: '14px', border: '1px solid #d9e2ec', borderRadius: '9px', padding: '12px' },
  responseLabel: { display: 'block', fontWeight: '800', fontSize: '13px' },
  responseHint: { color: '#829ab1', fontSize: '11px', marginBottom: '8px' },
  responseBtn: {
    marginTop: '10px', border: 'none', borderRadius: '7px', padding: '7px 11px',
    background: '#215b7d', color: 'white', fontWeight: '700', cursor: 'pointer'
  },
  responseBox: { marginBottom: '14px', border: '1px solid #d9e2ec', borderRadius: '9px', padding: '12px', background: '#fbfcfd' },
  aiDisclosurePanel: { marginTop: '14px', border: '1px solid #cfd8df', borderRadius: '9px', padding: '12px', background: '#f8fafb' },
  aiDisclosureHeading: { fontWeight: '800', fontSize: '14px', marginBottom: '8px' },
  aiQuestion: { fontSize: '13px', fontWeight: '600', marginBottom: '8px' },
  radioRow: { display: 'flex', gap: '18px', marginBottom: '8px' },
  radioLabel: { display: 'flex', gap: '6px', alignItems: 'center', fontSize: '13px' },
  aiYesArea: { marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #d7dee5' },
  confirmationRow: { display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', alignItems: 'start', fontSize: '12px', lineHeight: 1.45 },
  infoBtn: {
    width: '22px', height: '22px', borderRadius: '50%', border: '1px solid #9fb5c4',
    background: 'white', color: '#215b7d', fontWeight: '800', cursor: 'pointer', padding: 0
  },
  urlRow: { display: 'flex', gap: '8px', marginTop: '10px' },
  urlInput: { flex: 1, minWidth: 0, border: '1px solid #cfd8df', borderRadius: '6px', padding: '7px 9px' },
  infoBox: { marginTop: '10px', border: '1px solid #c7d6e0', borderRadius: '8px', padding: '11px', background: 'white' },
  infoBoxTitle: { fontWeight: '700', fontSize: '12px', marginBottom: '7px' },
  infoText: { fontSize: '12px', color: '#526474', lineHeight: 1.45 },
  noAiAllowed: { background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '7px', padding: '9px', color: '#a12a2a', fontSize: '12px', marginBottom: '8px' },
  permissionList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  permissionItem: { borderTop: '1px solid #eef2f5', paddingTop: '7px' },
  permissionTitle: { fontWeight: '800', fontSize: '12px', color: '#215b7d' },
  permissionDescription: { fontSize: '11px', color: '#526474', marginTop: '2px' },
  permissionNote: { fontSize: '11px', color: '#7a5310', marginTop: '3px' },
  aiDisclosureReadOnly: { marginTop: '10px', borderTop: '1px solid #e5eaee', paddingTop: '10px', fontSize: '12px', color: '#486581' },
  aiDisclosureLinkRow: { marginTop: '4px', wordBreak: 'break-word' },
  modalOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.58)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 400, padding: '20px'
  },
  modalPanel: {
    width: '100%', maxWidth: '720px', maxHeight: '90vh', overflowY: 'auto',
    background: 'white', borderRadius: '10px', boxShadow: '0 18px 50px rgba(0,0,0,0.25)'
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px',
    padding: '16px 18px', borderBottom: '1px solid #e5eaee'
  },
  modalTitle: { fontSize: '18px', fontWeight: '800', color: '#243b53' },
  modalSubtitle: { fontSize: '12px', color: '#627d98', marginTop: '2px' },
  modalIntro: { padding: '14px 18px 8px', fontSize: '13px', color: '#486581' },
  modalError: { margin: '12px 18px 0', background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '7px', padding: '9px 10px', color: '#a12a2a', fontSize: '12px' },
  modalOptionList: { padding: '6px 18px 12px', display: 'flex', flexDirection: 'column', gap: '7px' },
  modalOption: {
    display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px', border: '1px solid #d9e2ec',
    borderRadius: '8px', background: '#fbfcfd'
  },
  modalOptionSelected: { border: '1px solid #9fc8ad', background: '#f3faf6' },
  modalOptionDisabled: { opacity: 0.58 },
  modalOptionMain: { minWidth: 0, flex: 1 },
  modalOptionHeading: { display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap' },
  modalOptionSide: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '12px 18px 16px', borderTop: '1px solid #e5eaee' },
  confirmChoiceList: { padding: '14px 18px 6px', display: 'flex', flexDirection: 'column', gap: '7px' },
  confirmChoiceRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 10px', background: '#f7f9fb', borderRadius: '7px' },
  finalWarning: { margin: '10px 18px 14px', padding: '12px', background: '#fff7e6', border: '1px solid #f1d39a', borderRadius: '8px', color: '#7a5310', fontSize: '13px', lineHeight: 1.45 },
  previewOverlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 500, padding: '20px'
  },
  previewPanel: {
    background: 'white', borderRadius: '8px', width: '100%', maxWidth: '800px', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column', overflow: 'hidden'
  },
  previewHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '16px 20px', borderBottom: '1px solid #d9e2ec' },
  previewTitle: { fontWeight: '700', fontSize: '16px', color: '#243b53' },
  previewAuthor: { fontSize: '13px', color: '#627d98', marginTop: '2px' },
  previewClose: { border: 'none', background: 'none', fontSize: '26px', cursor: 'pointer', color: '#627d98', lineHeight: 1 },
  previewFrame: { flex: 1, border: 'none', minHeight: '500px' },
  previewNoFile: { padding: '20px', color: '#627d98', fontSize: '14px' },
  downloadLink: { display: 'block', padding: '12px 20px', color: '#2f6f9f', fontSize: '14px', fontWeight: '600', textDecoration: 'underline', borderTop: '1px solid #d9e2ec' }
};
